import { ReactNode, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { getSpecialOrder } from "../services";
import { SpecialOrderDetail } from "../types/special-order.type";
import { formatNumber } from "../utils";
import { ModeChip, StatusChip } from "../components/SpecialOrder/Chips";
import CustomerTermsChips from "../components/SpecialOrder/CustomerTermsChips";
import StatusStepper from "../components/SpecialOrder/StatusStepper";
import TotalsSummary from "../components/SpecialOrder/TotalsSummary";
import ActionPanel from "../components/SpecialOrder/ActionPanel";
import {
  DOC_TYPE_LABEL,
  SPECIAL_ORDER_PATH,
  billtypeLabel,
  deliveryDateLabel,
  formatOrderDate,
} from "../components/SpecialOrder/data";

const Field = ({ label, value }: { label: string; value: ReactNode }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} component="div">
      {value}
    </Typography>
  </Box>
);

export default function SpecialOrderDetailPage() {
  const { uuid } = useParams<{ uuid: string }>();
  const [order, setOrder] = useState<SpecialOrderDetail | null>(null);
  const [notFound, setNotFound] = useState<boolean>(false);

  const fetchOrder = useCallback(async () => {
    if (!uuid) return;
    try {
      const {
        data: { data },
      } = await getSpecialOrder(uuid);
      setOrder(data ?? null);
      setNotFound(!data);
    } catch {
      setNotFound(true);
    }
  }, [uuid]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const breadcrumb = (
    <Box display="flex" alignItems="center" mb={2}>
      <Link to={SPECIAL_ORDER_PATH} style={{ textDecoration: "none", color: "inherit" }}>
        <Typography variant="body1" fontWeight={500}>
          ออเดอร์พิเศษ
        </Typography>
      </Link>
      <Typography variant="body1" sx={{ mx: 1 }}>
        /
      </Typography>
      <Typography variant="body1" color="primary">
        {order?.order_no ?? "…"}
      </Typography>
    </Box>
  );

  if (!order) {
    return (
      <Box sx={{ padding: "8px 0" }}>
        {breadcrumb}
        {notFound ? (
          <Typography color="text.secondary">ไม่พบออเดอร์พิเศษนี้</Typography>
        ) : (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  }

  const lines = order.items.filter((item) => item.qty > 0);

  return (
    <Box sx={{ padding: "8px 0" }}>
      {breadcrumb}

      <Paper sx={{ mb: 2 }}>
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6" fontWeight={700}>
                {order.order_no}
              </Typography>
              <StatusChip status={order.status} />
              <ModeChip mode={order.mode} />
            </Box>
            <Typography variant="body1" mt={0.5}>
              {order.customer.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {order.customer.customer_code}
              {order.customer.address ? ` · ${order.customer.address}` : ""}
            </Typography>
            <Box mt={1}>
              <CustomerTermsChips
                customer={order.customer}
                taxtype={order.taxtype}
                discountPercent={order.discount_percent}
              />
            </Box>
          </Box>
          <Box display="flex" gap={3} flexWrap="wrap" alignItems="flex-start">
            <Field
              label="สาย"
              value={`${order.route.route_code}${order.route.route_name ? ` · ${order.route.route_name}` : ""}`}
            />
            <Field label={deliveryDateLabel(order.mode)} value={formatOrderDate(order.delivery_date)} />
            <Field label="ประเภทบิล" value={billtypeLabel(order.customer.billtype)} />
            <Field
              label="เลขบิล"
              value={
                order.reserved_transaction_no ? (
                  <>
                    {order.reserved_transaction_no}
                    {order.doc_type && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {DOC_TYPE_LABEL[order.doc_type]}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    ยังไม่จอง
                  </Typography>
                )
              }
            />
          </Box>
        </Box>
        <Box mt={3}>
          <StatusStepper order={order} />
        </Box>
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 0, overflow: "hidden", mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} p={2}>
              รายการสินค้า ({lines.length} รายการ)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
                  <TableRow>
                    <TableCell width={48}>#</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell>บาร์โค้ด</TableCell>
                    <TableCell align="right">ราคา/หน่วย</TableCell>
                    <TableCell align="right">จำนวน</TableCell>
                    <TableCell align="right">จำนวนเงิน</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((item, index) => (
                    <TableRow key={item.product_uuid}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.product_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.product_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.barcode || "-"}</TableCell>
                      <TableCell align="right">{formatNumber(item.unit_price)}</TableCell>
                      <TableCell align="right">{formatNumber(item.qty, { decimals: 0 })}</TableCell>
                      <TableCell align="right">{formatNumber(item.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box display="flex" justifyContent="flex-end" p={2}>
              <Box width={{ xs: "100%", sm: 320 }}>
                <TotalsSummary
                  subtotal={order.subtotal}
                  discountPercent={order.discount_percent}
                  discountAmount={order.discount_amount}
                  totalNoTax={order.total_no_tax}
                  taxAmount={order.tax_amount}
                  grandTotal={order.grand_total}
                />
              </Box>
            </Box>
          </Paper>
          {order.note && (
            <Paper>
              <Typography variant="caption" color="text.secondary">
                หมายเหตุ
              </Typography>
              <Typography variant="body2" whiteSpace="pre-wrap">
                {order.note}
              </Typography>
            </Paper>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <ActionPanel order={order} onChanged={fetchOrder} />
        </Grid>
      </Grid>
    </Box>
  );
}
